/**
 * Hazard gameplay effects: eruptions, geyser boosts, crystal shatter, sonora resonance, proximity.
 * Pure functions — no side effects, no Three.js imports.
 */

import type { GameState, PlayerState, PlanetState, StructureState, HazardState, Vec3 } from '../types';
import { layoutOfStructure } from './modules';
import { blastModules, moduleDistances } from './moduleDamage';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { damageUnit, damageStructure } from './damage';
import { vecLength, vecSub, vecNormalize, vecAdd, vecScale } from '../math';
import { getHazardWorldPosition, getPlayerWorldPosition } from './gameState';
import { SeededRandom } from './random';
import {
  VOLCANIC_ERUPTION_CHANCE,
  VOLCANIC_ERUPTION_DAMAGE,
  VOLCANIC_ERUPTION_RADIUS,
  VOLCANIC_ERUPTION_KNOCKBACK,
  VOLCANIC_PROXIMITY_DAMAGE,
  VOLCANIC_PROXIMITY_RADIUS,
  GEYSER_BOOST_RADIUS,
  GEYSER_BOOST_VELOCITY,
  GEYSER_PROXIMITY_RADIUS,
  GEYSER_LAUNCH_CHANCE,
  GEYSER_LAUNCH_VELOCITY,
  CRYSTAL_SHATTER_DAMAGE,
  CRYSTAL_SHATTER_RADIUS,
  SONORA_CRATER_MULTIPLIER,
  SONORA_KNOCKBACK_MULTIPLIER,
} from '../constants';

// =============================================================================
// VOLCANIC ERUPTIONS
// =============================================================================

export interface EruptionResult {
  players: PlayerState[];
  damages: Array<{ playerId: number; damage: number }>;
  knockbacks: Array<{ playerId: number; launched: boolean }>;
  eruptedHazardIds: number[];
}

export function processVolcanicEruptions(
  players: PlayerState[],
  planets: PlanetState[],
  hazards: HazardState[],
  gameSeed: string,
  turnNumber: number
): EruptionResult {
  const rng = new SeededRandom(`${gameSeed}-eruptions-${turnNumber}`);
  const eruptedHazardIds: number[] = [];
  const damages: Array<{ playerId: number; damage: number }> = [];
  const knockbacks: Array<{ playerId: number; launched: boolean }> = [];

  // Determine which volcanic hazards erupt
  for (const hazard of hazards) {
    if (!hazard.alive || hazard.hazardType !== 'volcanic') continue;
    if (rng.next() < VOLCANIC_ERUPTION_CHANCE) {
      eruptedHazardIds.push(hazard.id);
    }
  }

  if (eruptedHazardIds.length === 0) {
    return { players, damages, knockbacks, eruptedHazardIds };
  }

  // Apply eruption effects to players
  let updatedPlayers = [...players];
  for (const hazardId of eruptedHazardIds) {
    const hazard = hazards.find(h => h.id === hazardId)!;
    const hazardPos = getHazardWorldPosition(hazard, planets);
    const surfaceNormal = vecNormalize(hazard.surfaceDir);

    updatedPlayers = updatedPlayers.map(player => {
      if (!player.alive || player.airborne) return player;

      const playerPos = getPlayerWorldPosition(player, planets);
      const dist = vecLength(vecSub(playerPos, hazardPos));

      if (dist >= VOLCANIC_ERUPTION_RADIUS) return player;

      // Damage falloff
      const factor = Math.max(0, 1 - dist / VOLCANIC_ERUPTION_RADIUS);
      const dmg = VOLCANIC_ERUPTION_DAMAGE * factor;

      if (dmg > 0.5) {
        damages.push({ playerId: player.id, damage: dmg });
      }

      let updated: PlayerState = damageUnit(player, dmg, 'hazard').unit;
      const alive = updated.alive;

      // Knockback: launch in surface normal direction
      if (alive && dist < VOLCANIC_ERUPTION_RADIUS * 0.8) {
        const launchPos = vecAdd(playerPos, vecScale(surfaceNormal, 0.5));
        updated = {
          ...updated,
          airborne: true,
          airbornePos: launchPos,
          airborneVel: vecScale(surfaceNormal, VOLCANIC_ERUPTION_KNOCKBACK * factor),
          airborneCause: 'hazard' as const,
        };
        knockbacks.push({ playerId: player.id, launched: true });
      }

      return updated;
    });
  }

  return { players: updatedPlayers, damages, knockbacks, eruptedHazardIds };
}

// =============================================================================
// GEYSER BOOST
// =============================================================================

export interface GeyserBoostResult {
  boosted: boolean;
  newVel: Vec3;
  geyserHazardId: number;
}

export function checkGeyserBoost(
  pos: Vec3,
  vel: Vec3,
  planets: PlanetState[],
  hazards: HazardState[],
  alreadyBoostedIds: Set<number>
): GeyserBoostResult | null {
  for (const hazard of hazards) {
    if (!hazard.alive || hazard.hazardType !== 'geyser') continue;
    if (alreadyBoostedIds.has(hazard.id)) continue;

    const hazardPos = getHazardWorldPosition(hazard, planets);
    const dist = vecLength(vecSub(pos, hazardPos));

    if (dist < GEYSER_BOOST_RADIUS) {
      const surfaceNormal = vecNormalize(hazard.surfaceDir);
      const boost = vecScale(surfaceNormal, GEYSER_BOOST_VELOCITY);
      const newVel = vecAdd(vel, boost);
      return { boosted: true, newVel, geyserHazardId: hazard.id };
    }
  }

  return null;
}

// =============================================================================
// CRYSTAL SHATTER
// =============================================================================

export interface CrystalShatterResult {
  players: PlayerState[];
  damages: Array<{ playerId: number; damage: number }>;
  knockbacks: Array<{ playerId: number; launched: boolean }>;
  structures: StructureState[];
  destroyedStructures: number[];
}

export function computeCrystalShatterDamage(
  crystal: HazardState,
  players: PlayerState[],
  planets: PlanetState[],
  structures: StructureState[],
  shooterTeamIndex?: number,
  friendlyFireScale?: number,
  /** 72.3: a building whose health is its modules' (`healthFrom`) takes the shatter on its modules (the config, the tick). */
  modular?: { config: GameState['config']; tick: number }
): CrystalShatterResult {
  const crystalPos = getHazardWorldPosition(crystal, planets);
  const damages: Array<{ playerId: number; damage: number }> = [];
  const knockbacks: Array<{ playerId: number; launched: boolean }> = [];
  const destroyedStructures: number[] = [];
  const ffScale = friendlyFireScale ?? 1.0;

  const newPlayers = players.map(player => {
    if (!player.alive || player.airborne) return player;

    const playerPos = getPlayerWorldPosition(player, planets);
    const dist = vecLength(vecSub(playerPos, crystalPos));

    if (dist >= CRYSTAL_SHATTER_RADIUS) return player;

    const isFriendly = shooterTeamIndex !== undefined && player.teamIndex === shooterTeamIndex;
    const damageMultiplier = isFriendly ? ffScale : 1.0;

    const factor = Math.max(0, 1 - dist / CRYSTAL_SHATTER_RADIUS);
    const dmg = CRYSTAL_SHATTER_DAMAGE * factor * damageMultiplier;

    if (dmg > 0.5) {
      damages.push({ playerId: player.id, damage: dmg });
    }

    let updated: PlayerState = damageUnit(player, dmg, 'hazard').unit;
    const alive = updated.alive;

    // Knockback away from crystal
    if (alive && factor > 0.3) {
      const knockDir = vecNormalize(vecSub(playerPos, crystalPos));
      const surfaceNormal = vecNormalize(player.surfaceDir);
      const launchPos = vecAdd(playerPos, vecScale(surfaceNormal, 0.5));
      updated = {
        ...updated,
        airborne: true,
        airbornePos: launchPos,
        airborneVel: vecScale(knockDir, 5.0 * factor),
        airborneCause: 'hazard' as const,
      };
      knockbacks.push({ playerId: player.id, launched: true });
    }

    return updated;
  });

  // Structure damage
  const newStructures = structures.map(structure => {
    if (structure.health <= 0) return structure;
    // 72.3: a building whose health is its modules' takes the shatter on them, each by its own distance (the table for its material)
    if (structure.healthFrom === 'modules' && modular) {
      const layout = layoutOfStructure(structure), dists = layout ? moduleDistances(crystalPos, structure, { planets, config: modular.config }) : null;
      if (!layout || !dists) return structure;
      const person = PERSON_BODY_HEIGHT * personSetOf(modular).k;
      return blastModules(structure, layout, dists, d => CRYSTAL_SHATTER_DAMAGE * Math.max(0, 1 - (d * person) / CRYSTAL_SHATTER_RADIUS), 'hazard', modular.tick);
    }

    const planet = planets[structure.planetIndex];
    const normal = vecNormalize(structure.surfaceDir);
    const structPos = vecAdd(planet.position, vecScale(normal, planet.radius));
    const dist = vecLength(vecSub(structPos, crystalPos));

    if (dist >= CRYSTAL_SHATTER_RADIUS) return structure;

    const factor = Math.max(0, 1 - dist / CRYSTAL_SHATTER_RADIUS);
    const dmg = CRYSTAL_SHATTER_DAMAGE * factor;
    const r = damageStructure(structure, dmg, 'hazard');
    if (r.destroyed) destroyedStructures.push(structure.id);
    return r.structure;
  });

  return { players: newPlayers, damages, knockbacks, structures: newStructures, destroyedStructures };
}

// =============================================================================
// SONORA RESONANCE
// =============================================================================

export interface SonoraModifiers {
  craterMult: number;
  knockbackMult: number;
}

export function getSonoraModifiers(planet: PlanetState): SonoraModifiers {
  if (planet.archetype === 'sonora') {
    return {
      craterMult: SONORA_CRATER_MULTIPLIER,
      knockbackMult: SONORA_KNOCKBACK_MULTIPLIER,
    };
  }
  return { craterMult: 1.0, knockbackMult: 1.0 };
}

// =============================================================================
// PROXIMITY EFFECTS
// =============================================================================

export interface ProximityResult {
  player: PlayerState;
  damage: number;
  launched: boolean;
  sourceHazardId: number | null;
}

export function processProximityEffects(
  player: PlayerState,
  planets: PlanetState[],
  hazards: HazardState[],
  gameSeed: string,
  turnNumber: number
): ProximityResult {
  if (!player.alive || player.airborne) {
    return { player, damage: 0, launched: false, sourceHazardId: null };
  }

  const rng = new SeededRandom(`${gameSeed}-proximity-${player.id}-${turnNumber}`);
  const playerPos = getPlayerWorldPosition(player, planets);
  let updatedPlayer = { ...player };
  let totalDamage = 0;
  let launched = false;
  let sourceHazardId: number | null = null;

  for (const hazard of hazards) {
    if (!hazard.alive) continue;

    const hazardPos = getHazardWorldPosition(hazard, planets);
    const dist = vecLength(vecSub(playerPos, hazardPos));

    if (hazard.hazardType === 'volcanic' && dist < VOLCANIC_PROXIMITY_RADIUS) {
      // Flat damage from heat
      totalDamage += VOLCANIC_PROXIMITY_DAMAGE;
      sourceHazardId = hazard.id;
    }

    if (hazard.hazardType === 'geyser' && dist < GEYSER_PROXIMITY_RADIUS && !launched) {
      // Chance to launch
      if (rng.next() < GEYSER_LAUNCH_CHANCE) {
        const surfaceNormal = vecNormalize(hazard.surfaceDir);
        const launchPos = vecAdd(playerPos, vecScale(surfaceNormal, 0.5));
        updatedPlayer = {
          ...updatedPlayer,
          airborne: true,
          airbornePos: launchPos,
          airborneVel: vecScale(surfaceNormal, GEYSER_LAUNCH_VELOCITY),
          airborneCause: 'hazard' as const,
        };
        launched = true;
        sourceHazardId = hazard.id;
      }
    }
  }

  if (totalDamage > 0) updatedPlayer = damageUnit(updatedPlayer, totalDamage, 'hazard').unit;

  return { player: updatedPlayer, damage: totalDamage, launched, sourceHazardId };
}
